"use client";

import { uploadSubmissionAudioAction } from "@/lib/actions/media";
import { getMuxUploadAvailableAction } from "@/lib/actions/mux-upload";
import { createSubmissionAction } from "@/lib/actions/submissions";
import { MUX_PENDING_TRACK_URL } from "@/lib/constants/audio-submissions";
import type { SubmissionVisibility } from "@/types/database";

export const AUDIO_ACCEPT = "audio/*,.mp3,.wav,.flac,.aiff,.aif,.m4a,.ogg";
export const MAX_AUDIO_SIZE = 50 * 1024 * 1024;

/** Why a file can't be uploaded, or null when it can. */
export function audioFileProblem(file: File): string | null {
	if (
		!(
			file.type.startsWith("audio/") ||
			/\.(mp3|wav|flac|aiff?|m4a|ogg)$/i.test(file.name)
		)
	)
		return "Choose an audio file: MP3, WAV, FLAC, AIFF, M4A or OGG.";
	if (file.size > MAX_AUDIO_SIZE) return "Audio files can be up to 50 MB.";
	return null;
}

export function titleFromFile(name: string) {
	return name
		.replace(/\.[a-z0-9]+$/i, "")
		.replace(/[_-]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

/**
 * Upload a track to the person's music (a submission with uploaded audio):
 * protected storage, or Mux when it's set up — the submission form's paths.
 * The person must already have confirmed the rights.
 */
export async function uploadTrack(input: {
	file: File;
	title: string;
	visibility: SubmissionVisibility;
}): Promise<
	{ ok: true; id: number } | { ok: false; error: string; id?: number }
> {
	const { enabled: mux } = await getMuxUploadAvailableAction().catch(() => ({
		enabled: false,
	}));
	let trackUrl = MUX_PENDING_TRACK_URL;
	if (!mux) {
		const form = new FormData();
		form.append("file", input.file);
		const stored = await uploadSubmissionAudioAction(form);
		if (!stored.success || !stored.data?.url)
			return { ok: false, error: stored.error ?? "Couldn’t upload the audio." };
		trackUrl = stored.data.url;
	}
	const created = await createSubmissionAction({
		track_name: input.title.trim() || "Untitled",
		track_url: trackUrl,
		embed_type: "uploaded",
		visibility: input.visibility,
		rights_attested: true,
		rights_attestation_text:
			"I am the artist or have permission to submit this music.",
		submission_origin: "member",
	});
	if (!created.success) return { ok: false, error: created.error };
	const id = created.data.id;
	if (mux) {
		const init = await fetch("/api/uploads/audio", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ submissionId: id }),
		});
		if (!init.ok)
			return {
				ok: false,
				id,
				error: "The track was added, but its audio couldn’t start uploading.",
			};
		const { uploadUrl } = (await init.json()) as { uploadUrl: string };
		const put = await fetch(uploadUrl, {
			method: "PUT",
			body: input.file,
			headers: {
				"Content-Type": input.file.type || "application/octet-stream",
			},
		});
		if (!put.ok)
			return {
				ok: false,
				id,
				error: "The track was added, but its audio didn’t finish uploading.",
			};
	}
	return { ok: true, id };
}
